import React, { useState } from 'react';
import { Plus, Edit, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useDataManager } from '../../hooks/useDataManager';

const CategoryManagement = () => {
  const { categories, addCategory, updateCategory, deleteCategory } = useDataManager();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    showInNavbar: false
  });

  const handleAddNew = () => {
    setEditingCategory(null);
    setFormData({ name: '', slug: '', description: '', showInNavbar: false });
    setIsDialogOpen(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData(category);
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure? This will not delete products in this category.')) {
      deleteCategory(id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCategory) {
      updateCategory(editingCategory.id, formData);
    } else {
      // When adding a new category, assign the next sequence number
      const maxSequence = categories.length > 0
        ? Math.max(...categories.map(c => c.sequence || 0))
        : 0;
      addCategory({
        ...formData,
        sequence: maxSequence + 1
      });
    }
    setIsDialogOpen(false);
  };

  const handleMoveUp = (category, index) => {
    if (index === 0) return; // Already at top

    const sortedCategories = getSortedCategories();
    const currentCategory = sortedCategories[index];
    const previousCategory = sortedCategories[index - 1];

    // Get or initialize sequence values
    const currentSeq = currentCategory.sequence ?? currentCategory.id;
    const previousSeq = previousCategory.sequence ?? previousCategory.id;

    // Swap sequences
    updateCategory(currentCategory.id, {
      ...currentCategory,
      sequence: previousSeq
    });
    updateCategory(previousCategory.id, {
      ...previousCategory,
      sequence: currentSeq
    });
  };

  const handleMoveDown = (category, index) => {
    const sortedCategories = getSortedCategories();
    if (index === sortedCategories.length - 1) return; // Already at bottom

    const currentCategory = sortedCategories[index];
    const nextCategory = sortedCategories[index + 1];

    // Get or initialize sequence values
    const currentSeq = currentCategory.sequence ?? currentCategory.id;
    const nextSeq = nextCategory.sequence ?? nextCategory.id;

    // Swap sequences
    updateCategory(currentCategory.id, {
      ...currentCategory,
      sequence: nextSeq
    });
    updateCategory(nextCategory.id, {
      ...nextCategory,
      sequence: currentSeq
    });
  };

  const getSortedCategories = () => {
    // Sort categories by sequence, fallback to id if sequence not set
    return [...categories].sort((a, b) => {
      const seqA = a.sequence ?? a.id;
      const seqB = b.sequence ?? b.id;
      return seqA - seqB;
    });
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Categories</h2>
          <p className="text-gray-600 mt-1">Manage product categories</p>
        </div>
        <Button
          onClick={handleAddNew}
          className="bg-[#4a1a1f] hover:bg-[#5a2a2f] text-white"
        >
          <Plus size={18} className="mr-2" />
          Add Category
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {getSortedCategories().map((category, index) => (
          <Card key={category.id} className="relative">
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-lg">{category.name}</CardTitle>
                <div className="flex flex-col gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleMoveUp(category, index)}
                    disabled={index === 0}
                    className="h-6 w-6 p-0"
                    title="Move up"
                  >
                    <ChevronUp size={14} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleMoveDown(category, index)}
                    disabled={index === getSortedCategories().length - 1}
                    className="h-6 w-6 p-0"
                    title="Move down"
                  >
                    <ChevronDown size={14} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-2">{category.description}</p>
              <p className="text-xs text-gray-400 mb-4 font-mono">Slug: {category.slug}</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(category)}
                >
                  <Edit size={14} className="mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(category.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Category Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    name: e.target.value,
                    slug: generateSlug(e.target.value)
                  });
                }}
                placeholder="Everyday Wear"
                required
              />
            </div>
            <div>
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="everyday-wear"
                required
              />
              <p className="text-xs text-gray-500 mt-1">URL-friendly identifier</p>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Category description"
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.showInNavbar || false}
                onCheckedChange={(checked) => setFormData({ ...formData, showInNavbar: checked })}
              />
              <Label>Show in Navigation Bar</Label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#4a1a1f] hover:bg-[#5a2a2f]">
                {editingCategory ? 'Update' : 'Add'} Category
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryManagement;
